// Core Modules
import { CommonModule } from '@angular/common';
import { AfterContentChecked, ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, NavigationStart, Router, RouterOutlet } from '@angular/router';

// External Modules
import { BehaviorSubject, filter, Subscription } from 'rxjs';
import { NgxPermissionsModule } from 'ngx-permissions';

// Components
import { BackToTopComponent } from '@shared/components/back-to-top/back-to-top.component';
import { FooterComponent } from '@shared/components/footer/footer.component';
import { HeaderComponent } from '@shared/components/header/header.component';
import { IgniteAssistantComponent } from '@shared/components/ignite-assistant/ignite-assistant.component';

// Tokens
import { WINDOW } from '@core/injection-tokens/tokens';

// Services
import { AuthService } from '@core/services/auth/auth.service';

// Enums
import { LocalStorageEnum } from '@shared/enum/local-storage-keys.enum';
import { MainNavigationEnum } from '@shared/enum/main-navigation-url.enum';
import { SubNavigationEnum } from '@shared/enum/sub-navigation-url.enum';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [CommonModule, RouterOutlet, BackToTopComponent, FooterComponent, HeaderComponent, IgniteAssistantComponent, NgxPermissionsModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, AfterContentChecked, OnDestroy {
  // Injectors
  private readonly changeDetector = inject(ChangeDetectorRef);
  private readonly router = inject(Router);
  private readonly window = inject(WINDOW);
  public readonly authService = inject(AuthService);

  // Private Members
  private readonly routerSubscription = new Subscription();
  private hideNavigationSubject = new BehaviorSubject<boolean>(true);
  /**
   * Whether the docs route is the one being loaded. Seeded from the current URL
   * rather than left false, because the first navigation's guards - and the
   * authentication they flag as in progress - run before any router event this
   * component could react to.
   */
  private isDocsRouteSubject = new BehaviorSubject<boolean>(this.isDocsUrl(this.window.location.pathname));

  // Public Members
  public hideNavigation$ = this.hideNavigationSubject.asObservable();
  public isDocsRoute$ = this.isDocsRouteSubject.asObservable();

  /**
   * Whether a URL addresses the docs route, matched on the path alone - the
   * reference rewrites the fragment as the reader moves through it.
   * @param url URL to test, with or without query string and fragment
   * @returns true when the URL addresses the docs route
   */
  private isDocsUrl(url: string): boolean {
    return url.split(/[?#]/)[0] === `/${MainNavigationEnum.DOCS}`;
  }

  public ngOnInit(): void {
    // Flagged at the start of navigation, not the end: PermissionGuard marks authentication
    // as in progress while it runs, and the header renders during that window. Waiting for
    // NavigationEnd would let the header appear over the docs route and then disappear.
    this.routerSubscription.add(
      this.router.events.pipe(filter((i) => i instanceof NavigationStart)).subscribe({
        next: (event: NavigationStart) => this.isDocsRouteSubject.next(this.isDocsUrl(event.url)),
        error: (e) => console.log(`<AppComponent> - <ngOnInit> Error occurred during subscribing navigation start events...`, e),
      })
    );
    // Clearing out save_audience and next_state information from session storage at entry level.
    this.routerSubscription.add(
      this.router.events.pipe(filter((i) => i instanceof NavigationEnd)).subscribe({
        next: (event: NavigationEnd) => {
          // The reference drives the fragment on the docs route, so it is matched on the path alone -
          // matching the whole URL would miss '/docs#description/introduction' and flip the shell mid-session.
          const isDocsRoute = this.isDocsUrl(event.urlAfterRedirects);
          this.isDocsRouteSubject.next(isDocsRoute);
          // If navigation involves login, client-list or docs route set 'hideNavigationSubject' to true
          if (isDocsRoute || [`/${MainNavigationEnum.LOGIN}`, `/${MainNavigationEnum.CLIENT_LIST}`, '/'].includes(event.urlAfterRedirects)) this.hideNavigationSubject.next(true);
          // set 'hideNavigationSubject' to false when end event does not include login or client-list
          else this.hideNavigationSubject.next(false);
          // If routeUrl does not include 'campaign-workflow/campaign' clear the local storage tab values
          if (!event.urlAfterRedirects.includes(`/${MainNavigationEnum.CAMPAIGN_WORKFLOW}/${SubNavigationEnum.CAMPAIGN}`)) {
            const clientId = this.authService.getClientId();
            // If clientId is present then remove the existing tab configurations present when user navigates out of campaigns route
            if (clientId) {
              localStorage.removeItem(`${LocalStorageEnum.CAMPAIGN_TABS}${clientId}`);
              localStorage.removeItem(`${LocalStorageEnum.CAMPAIGN_ACTIVE_TACTICS}${clientId}`);
              localStorage.removeItem(`${LocalStorageEnum.CAMPAIGN_ACTIVE_AUD}${clientId}`);
            }
          }
        },
        error: (e) => {
          console.log(`<AppComponent> - <ngOnInit> Error occurred during subscribing router events...`, e);
          this.hideNavigationSubject.next(false);
        },
        complete: () => this.hideNavigationSubject.complete(),
      })
    );
  }

  /**
   * Respond after Angular checks the content projected into the directive or component.
   */
  public ngAfterContentChecked(): void {
    this.changeDetector.detectChanges();
  }

  public ngOnDestroy(): void {
    this.routerSubscription.unsubscribe();
    this.authService.authenticationInProgressSubject.unsubscribe();
    this.authService.clientNavigationInProgressSubject.unsubscribe();
  }
}
